// =============================================================================
// Client-side API for shared fuel logs ("Tank Liste").
//
// Schema (see supabase/schema.sql section 9):
//   fuel_logs         (id, name, owner_id, currency, created_at)
//   fuel_log_members  (log_id, user_id, joined_at)
//   fuel_log_invites  (token, log_id, created_by, created_at)
//   fuel_entries      (id, log_id, user_id, entry_date, odometer,
//                      fuel_cost, liters, note, created_at)
//
// Settlement model
// ----------------
// Entries are sorted by `odometer` ascending (the car's odometer is
// strictly monotonic in real life). The km between two consecutive entries
// is attributed to whoever logged the *later* entry — convention: you log
// when you finish driving.
//
// A "fill-up" is any entry with `fuel_cost > 0`. When a fill-up happens,
// the cost is split among drivers proportionally to the km each person
// drove since the previous fill-up (or since the very first entry, if no
// previous fill-up exists). The fill-up payer paid the whole bill, so the
// other drivers owe their share to the payer.
//
// `computeBalances(entries)` returns:
//   {
//     totals: { [userId]: { km, paid, owesNet } },
//     // owesNet > 0 = this user is owed money; < 0 = they owe.
//     // When two users net out the diff is the amount one owes the other.
//   }
// =============================================================================
import { supabase } from './supabase';

const buildInviteUrl = (token) => {
  const { origin, pathname } = window.location;
  const base = `${origin}${pathname.replace(/\/+$/, '')}`;
  return `${base}?joinFuelLog=${encodeURIComponent(token)}`;
};

// -------- Logs ---------------------------------------------------------------

// Returns [{ id, name, currency, ownerId, isOwner, createdAt }].
export async function listFuelLogs() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('fuel_logs')
    .select('id, name, currency, owner_id, created_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    currency: r.currency || 'EUR',
    ownerId: r.owner_id,
    isOwner: r.owner_id === user.id,
    createdAt: r.created_at
  }));
}

// Create a log; the RPC also adds the creator as a member and provisions
// a reusable invite token. Returns { id, name, currency, inviteToken }.
export async function createFuelLog(name, currency = 'EUR') {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Name is required');
  const { data, error } = await supabase.rpc('create_fuel_log', {
    log_name: trimmed,
    log_currency: currency || 'EUR'
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No row returned from create_fuel_log');
  return {
    id: row.id,
    name: row.name,
    currency: row.currency || 'EUR',
    inviteToken: row.invite_token
  };
}

// Rename / change currency. Owner-only (RLS enforced).
export async function updateFuelLog(logId, patch) {
  const row = {};
  if (patch.name !== undefined) {
    const t = (patch.name || '').trim();
    if (!t) throw new Error('Name is required');
    row.name = t;
  }
  if (patch.currency !== undefined) {
    row.currency = (patch.currency || 'EUR').toUpperCase().slice(0, 8);
  }
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('fuel_logs')
    .update(row)
    .eq('id', logId);
  if (error) throw error;
}

// Delete (owner only). Cascades to members, invites, entries.
export async function deleteFuelLog(logId) {
  const { error } = await supabase.from('fuel_logs').delete().eq('id', logId);
  if (error) throw error;
}

// -------- Invites ------------------------------------------------------------

export async function getFuelLogInviteUrl(logId) {
  const { data, error } = await supabase
    .from('fuel_log_invites')
    .select('token')
    .eq('log_id', logId)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  if (data) return buildInviteUrl(data.token);
  // No token yet — owners can create one (RLS enforces ownership).
  const { data: { user } } = await supabase.auth.getUser();
  // Use the same compact base36 random scheme as shared_calendars (so the
  // token shape is consistent across resources).
  const arr = new Uint8Array(24);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  const token = Array.from(arr, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 28);
  const { error: insErr } = await supabase
    .from('fuel_log_invites')
    .insert({ token, log_id: logId, created_by: user.id });
  if (insErr) throw insErr;
  return buildInviteUrl(token);
}

export async function joinFuelLogByToken(token) {
  const { data, error } = await supabase.rpc('join_fuel_log', {
    invite_token: token
  });
  if (error) throw error;
  return data; // uuid
}

// -------- Members ------------------------------------------------------------

export async function listFuelLogMembers(logId) {
  const { data, error } = await supabase.rpc('list_fuel_log_members', {
    lid: logId
  });
  if (error) throw error;
  return (data || []).map((r) => ({
    userId: r.user_id,
    email: r.email,
    displayName: r.display_name,
    avatarEmoji: r.avatar_emoji,
    avatarColor: r.avatar_color,
    avatarUrl: r.avatar_url,
    avatarKind: r.avatar_kind || 'emoji',
    isOwner: r.is_owner,
    joinedAt: r.joined_at
  }));
}

export async function leaveFuelLog(logId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { error } = await supabase
    .from('fuel_log_members')
    .delete()
    .eq('log_id', logId)
    .eq('user_id', user.id);
  if (error) throw error;
}

// -------- Entries ------------------------------------------------------------

// Returns entries sorted by odometer ascending (the natural settlement
// order). Caller can reverse for display if desired.
export async function listFuelEntries(logId) {
  const { data, error } = await supabase
    .from('fuel_entries')
    .select('id, log_id, user_id, entry_date, odometer, fuel_cost, liters, note, created_at')
    .eq('log_id', logId)
    .order('odometer', { ascending: true })
    .order('entry_date', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    logId: r.log_id,
    userId: r.user_id,
    entryDate: r.entry_date,
    odometer: r.odometer,
    fuelCost: r.fuel_cost == null ? null : Number(r.fuel_cost),
    liters: r.liters == null ? null : Number(r.liters),
    note: r.note || '',
    createdAt: r.created_at
  }));
}

export async function addFuelEntry(logId, entry) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const row = {
    log_id: logId,
    user_id: user.id,
    entry_date: entry.entryDate,
    odometer: Math.round(Number(entry.odometer)),
    fuel_cost: entry.fuelCost == null || entry.fuelCost === ''
      ? null
      : Number(entry.fuelCost),
    liters: entry.liters == null || entry.liters === ''
      ? null
      : Number(entry.liters),
    note: (entry.note || '').trim() || null
  };
  if (!row.entry_date) throw new Error('Datum fehlt');
  if (!Number.isFinite(row.odometer) || row.odometer < 0)
    throw new Error('Kilometerstand fehlt');

  const { data, error } = await supabase
    .from('fuel_entries')
    .insert(row)
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function deleteFuelEntry(entryId) {
  const { error } = await supabase
    .from('fuel_entries')
    .delete()
    .eq('id', entryId);
  if (error) throw error;
}

// -------- Settlement math ----------------------------------------------------

/**
 * Given an array of entries (sorted by odometer asc), compute settlement.
 *
 * Returns:
 *   {
 *     perUser: { [userId]: { km: number, paid: number, owed: number } },
 *     // `owed` is what they paid for fill-ups MINUS their km share of the
 *     // total. Positive = they're owed; negative = they owe.
 *     pairwise: [{ from: userId, to: userId, amount: number }]  // 2-user net
 *     fillUpCount: number,
 *     totalKm: number,
 *     unsettledKm: number  // km after the last fill-up that aren't paid for yet
 *   }
 */
export function computeBalances(entries) {
  const sorted = [...entries].sort((a, b) =>
    a.odometer === b.odometer
      ? String(a.entryDate).localeCompare(String(b.entryDate))
      : a.odometer - b.odometer
  );

  const perUser = {};
  const ensure = (uid) => {
    if (!perUser[uid]) perUser[uid] = { km: 0, paid: 0, owed: 0 };
    return perUser[uid];
  };

  // First pass: km attribution. The first entry in the log establishes a
  // baseline; subsequent entries' delta is attributed to the entry author.
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    const delta = Math.max(0, cur.odometer - prev.odometer);
    ensure(cur.userId).km += delta;
  }

  // Second pass: per-fill-up settlement.
  let lastFillUpIdx = -1; // index of previous fill-up (-1 = none yet)
  let fillUpCount = 0;
  for (let i = 0; i < sorted.length; i++) {
    const e = sorted[i];
    if (e.fuelCost == null || e.fuelCost <= 0) continue;
    fillUpCount += 1;

    // km driven in this billing window, broken down per driver. Window =
    // entries (lastFillUpIdx, i] (exclusive lower, inclusive upper). The
    // delta of entry j vs entry j-1 belongs to entry j's author.
    const windowKmByUser = {};
    let windowKm = 0;
    const startIdx = Math.max(0, lastFillUpIdx);
    // If lastFillUpIdx === -1 we want from index 0 onward; the delta of
    // index 0 has no predecessor so it contributes 0 km — which is the
    // intended behaviour (unknown baseline).
    for (let j = Math.max(1, startIdx + 1); j <= i; j++) {
      const prev = sorted[j - 1];
      const cur = sorted[j];
      const delta = Math.max(0, cur.odometer - prev.odometer);
      windowKm += delta;
      windowKmByUser[cur.userId] =
        (windowKmByUser[cur.userId] || 0) + delta;
    }

    // Payer: the user who logged this fill-up. They paid the full cost.
    ensure(e.userId).paid += e.fuelCost;
    // Each driver's share of the cost is proportional to their km in the
    // window. If windowKm is 0 (no recorded driving — unusual), split
    // equally among members who appeared in the log up to this point.
    if (windowKm > 0) {
      for (const [uid, km] of Object.entries(windowKmByUser)) {
        const share = e.fuelCost * (km / windowKm);
        ensure(uid).owed -= share;
      }
    }
    // The payer's `owed` is reduced by their own share (above) but they
    // also paid the full amount, so credit them back the full cost:
    ensure(e.userId).owed += e.fuelCost;

    lastFillUpIdx = i;
  }

  // Total km logged across the whole period.
  let totalKm = 0;
  for (let i = 1; i < sorted.length; i++) {
    totalKm += Math.max(0, sorted[i].odometer - sorted[i - 1].odometer);
  }
  // Km after the last fill-up not yet billed (visible as a "pending"
  // amount in the UI).
  let unsettledKm = 0;
  if (lastFillUpIdx === -1) {
    unsettledKm = totalKm;
  } else {
    unsettledKm = sorted.length
      ? Math.max(0, sorted[sorted.length - 1].odometer - sorted[lastFillUpIdx].odometer)
      : 0;
  }

  // For the common 2-user case, summarise into a single "X owes Y €Z" arrow.
  const userIds = Object.keys(perUser);
  const pairwise = [];
  if (userIds.length === 2) {
    const [a, b] = userIds;
    const aOwed = perUser[a].owed;
    const bOwed = perUser[b].owed;
    // Whoever has negative owed amount owes the other the absolute value.
    if (aOwed < -0.005) {
      pairwise.push({ from: a, to: b, amount: -aOwed });
    } else if (bOwed < -0.005) {
      pairwise.push({ from: b, to: a, amount: -bOwed });
    }
  } else if (userIds.length > 2) {
    // Naive multi-user settlement: each negative-balance user owes the
    // most-positive user, up to either's amount. Iterate.
    const debts = userIds.map((u) => ({ u, v: perUser[u].owed }));
    while (true) {
      debts.sort((x, y) => x.v - y.v);
      const debtor = debts[0];
      const creditor = debts[debts.length - 1];
      if (debtor.v >= -0.005 || creditor.v <= 0.005) break;
      const amt = Math.min(-debtor.v, creditor.v);
      pairwise.push({ from: debtor.u, to: creditor.u, amount: amt });
      debtor.v += amt;
      creditor.v -= amt;
    }
  }

  return { perUser, pairwise, fillUpCount, totalKm, unsettledKm };
}
